import { useState, useEffect, useLayoutEffect, useRef, lazy, Suspense, createContext, useContext, useCallback } from 'react';
import '@/App.css';
import '@/styles/index.css';
import '@/styles/transitions/FilmstripTransition.css';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, useParams, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Shield, Zap, RotateCw, MessageCircle } from 'lucide-react';
import ErrorBoundary from '@/components/ErrorBoundary';
import { CartProvider, useCart } from '@/context/CartContext';
import { CheckoutProvider, useCheckout } from '@/context/CheckoutContext';
import { ProductsProvider, useProducts } from '@/context/ProductsContext';
import { AdminProvider } from '@/context/AdminContext';
import { DiscordProvider } from '@/context/DiscordContext';
import { LiteModeProvider } from '@/context/LiteModeContext';
import CookieConsentBanner from '@/components/CookieConsentBanner';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { normalizeProductIdentifier } from '@/data/productIdentifier';
import {
  FOOTBALL_PRODUCT_PATH,
  isGemPackage
} from '@/data/retroFootball';

// Critical components - loaded immediately (above the fold)
import Navigation from '@/components/layout/Navigation';
/* The hero is the catalogue: every product is a card in the row, so it is
   imported directly rather than lazily — deferring the first thing on the
   page would only push back the largest paint. */
import ProductHero from '@/components/AngledCarousel';
/* Not lazy: the shaft has to paint on the frame the button is pressed. */
import LiftTransition, { FULL, EXPRESS, liftTotal, measureNavFloors } from '@/components/LiftTransition';
import StorefrontOnly from '@/components/StorefrontOnly';

// Lazy load below-the-fold components for better initial load
const Footer = lazy(() => import('@/components/layout/Footer'));
const ReviewsSection = lazy(() => import('@/components/sections/ReviewsSection'));
// Cart sidebar - slide-in panel
const CartSidebar = lazy(() => import('@/components/CartSidebar'));
const ScrollToTopButton = lazy(() => import('@/components/ScrollToTopButton'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
// Recent Buyers Widget
const RecentBuyersWidget = lazy(() => import('@/components/RecentBuyersWidget'));
const LazyToaster = lazy(() => import('sonner').then((m) => ({ default: m.Toaster })));

// Lazy load page components with preload capability
const Reviews = lazy(() => import('./pages/Reviews'));
const Team = lazy(() => import('./pages/Team'));
const AllProductsPageImport = () => import('./pages/AllProductsPage');
const AllProductsPage = lazy(AllProductsPageImport);

/* Kicked off the moment the call button is touched, so the ride is spent
   fetching the route rather than just waiting out an animation. Without it
   the doors would open onto a Suspense spinner. */
export const preloadProducts = () => { AllProductsPageImport(); };
const BundlesPage = lazy(() => import('./pages/BundlesPage'));
const CustomProductPageImport = () => import('./pages/CustomProductPage');
const CustomProductPage = lazy(CustomProductPageImport);


// Checkout pages
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess'));
const CheckoutCancel = lazy(() => import('./pages/CheckoutCancel'));
const CheckoutAuthReturn = lazy(() => import('./pages/CheckoutAuthReturn'));

// Admin pages
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const MetaGeneratorPage = lazy(() => import('./pages/admin/MetaGeneratorPage'));
const VersioningPage = lazy(() => import('./pages/admin/VersioningPage'));

// Checkout components
const AuthenticationWidget = lazy(() => import('./components/checkout/AuthenticationWidget'));
const CheckoutLoadingOverlay = lazy(() => import('./components/CheckoutLoadingOverlay'));
// CheckoutModal removed - using native Tebex.js overlay instead

// Discord components
const DiscordConnectModal = lazy(() => import('./components/discord/DiscordConnectModal'));

// Preload the correct product page experience for smooth transitions.
export const preloadProductPage = () => CustomProductPageImport();

const ProductRoute = () => {
  const { productId } = useParams();
  const { iapProducts = [] } = useProducts();
  const normalizedProductId = normalizeProductIdentifier(productId);
  const isGemRoute = isGemPackage(normalizedProductId)
    || iapProducts.some(product => (
      isGemPackage(product)
      && [
        product.slug,
        product.editorial?.slug,
        ...(product.aliases || []),
        ...(product.editorial?.aliases || [])
      ].some(identifier => normalizeProductIdentifier(identifier) === normalizedProductId)
    ));

  if (isGemRoute) return <Navigate to={FOOTBALL_PRODUCT_PATH} replace />;
  return <CustomProductPage key={productId} />;
};

// ========================================
// PAGE TRANSITION CONTEXT
// Premium Filmstrip Slide-Up Effect
// ========================================
const PageTransitionContext = createContext();

export const usePageTransition = () => {
  const context = useContext(PageTransitionContext);
  if (!context) {
    return { navigateWithTransition: null, navigateAsElevator: null, transitionPhase: 'idle', liftActive: false };
  }
  return context;
};

const getLocationDescriptor = (target, fallbackLocation) => {
  if (typeof target === 'string') {
    const baseOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://retrostore.dev';
    const url = new URL(target, baseOrigin);

    return {
      pathname: url.pathname,
      search: url.search,
      hash: url.hash
    };
  }

  return {
    pathname: target?.pathname || fallbackLocation.pathname,
    search: target?.search || '',
    hash: target?.hash || ''
  };
};

const getLocationSignature = (location) => (
  `${location?.pathname || ''}${location?.search || ''}${location?.hash || ''}`
);

/* Whether two locations are the same page.
 *
 * The query string is deliberately not part of this. /products?filter=Peds is
 * the same page as /products with a different subset of it showing — running
 * the whole document through a page transition for that is what made changing
 * a category feel like a shutter firing. */
const isSamePage = (a, b) => (
  (a?.pathname || '') === (b?.pathname || '') && (a?.hash || '') === (b?.hash || '')
);

const Home = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [scrollDirection, setScrollDirection] = useState('up'); // 'up' or 'down'

  // Use global cart context
  const { cartItems, setCartItems, isCartOpen, setIsCartOpen, addToCart, removeFromCart, updateQuantity, getTotalPrice, getTotalItems } = useCart();

  // Get products from context
  const { filters } = useProducts();
  
  // Mobile detection for responsive behavior
  const [isMobile, setIsMobile] = useState(false);
  
  const KEYMASTER_LOGIN_URL = 'https://keymaster.fivem.net/login';

  // Scroll to top button visibility with direction detection
  // Shows when scrolling UP after 200px, hides when scrolling DOWN
  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const direction = currentScrollY > lastScrollY ? 'down' : 'up';
          
          // Update direction state
          setScrollDirection(direction);
          
          // Show button only when:
          // 1. Scrolled past 200px (threshold)
          // 2. AND scrolling UP
          if (currentScrollY > 200 && direction === 'up') {
            setShowScrollTop(true);
          } else if (direction === 'down' || currentScrollY <= 200) {
            setShowScrollTop(false);
          }
          
          lastScrollY = currentScrollY;
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);



  // Get the filmstrip transition function
  const { navigateWithTransition } = usePageTransition();

  // Navigate to product page with filmstrip transition
  const navigateToProduct = (product) => {
    const productPath = product.slug || product.id;
    if (navigateWithTransition) {
      navigateWithTransition(`/product/${productPath}`);
    } else {
      navigate(`/product/${productPath}`);
    }
  };

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Features data with icons
  // Written to answer what a server owner actually asks before buying, rather
  // than the generic "premium quality / 24-7 support" set. Every claim here is
  // backed by something already on the site: escrow badges on product pages,
  // framework tags in the catalogue, the public changelog, and the Discord.


  const scrollToHome = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePurchase = () => {
    if (!isAuthenticated) {
      window.open(KEYMASTER_LOGIN_URL, '_blank');
      return;
    }
    setPurchaseLoading(true);
    setTimeout(() => setPurchaseLoading(false), 1200);
  };

  // Cart functions are now provided by CartContext (removeFromCart, updateQuantity, getTotalPrice, getTotalItems)

  return (
    <div className="min-h-screen bg-black text-gray-100">
      <Helmet>
        <title>RetroStore | Premium FiveM Scripts, Maps & Assets</title>
        <meta
          name="description"
          content="RetroStore offers premium FiveM scripts, maps, peds, and vehicles optimized for performance with lifetime updates and support."
        />
        <link rel="canonical" href="https://retrostore.dev/" />
      </Helmet>

      {/* Navigation */}
      <Navigation 
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        isCartOpen={isCartOpen}
        setIsCartOpen={setIsCartOpen}
        isAuthenticated={isAuthenticated}
        KEYMASTER_LOGIN_URL={KEYMASTER_LOGIN_URL}
        scrollToHome={scrollToHome}
        getTotalItems={getTotalItems}
      />

      {/* Main Content */}
      {/* The shell already renders <main id="main-content"> around the
          router; this one made two. */}
      <div id="home">
        {/* The hero is the catalogue now: every product is a card in the
            carousel, so the separate featured-products grid said the same
            thing twice. The stats band that follows it carries the proof
            that the "why choose us" section used to carry. */}
        <ProductHero />

        {/* Below-the-fold sections - Lazy loaded */}
        <Suspense fallback={<div className="min-h-[400px]" />}>
          {/* Discord Reviews Section */}
          <ReviewsSection />

          {/* Footer */}
          <Footer />
        </Suspense>
      </div>

      {/* Cart Sidebar - Premium slide-in panel */}
      <Suspense fallback={null}>
        <CartSidebar />
      </Suspense>

      {/* Scroll to Top Button - Lazy loaded */}
      <Suspense fallback={null}>
        <ScrollToTopButton 
          showScrollTop={showScrollTop}
          scrollToTop={scrollToTop}
          scrollDirection={scrollDirection}
        />
      </Suspense>
    </div>
  );
};

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [transitionPhase, setTransitionPhase] = useState('idle'); // 'idle' | 'exit' | 'enter'
  const [showVignette, setShowVignette] = useState(false);
  const [showFlash, setShowFlash] = useState(false);
  const pendingLocationRef = useRef(null);
  const isFilmstripTransition = useRef(false);

  /* The lift is deliberately kept out of transitionPhase: that variable drives
     the filmstrip's opacity fades, this one drives the page's travel, and
     folding them together would mean every filmstrip rule had to learn about
     floors. */
  const [lift, setLift] = useState(null);
  const liftTargetRef = useRef(null);
  const liftRidesRef = useRef(0);
  const liftTimersRef = useRef([]);

  /* Cloudflare injects route metadata into the raw app shell for crawlers and
     link previews. Once React owns the document head, Helmet supplies the same
     route-specific tags and these marked server fallbacks must not remain as
     duplicates. */
  useLayoutEffect(() => {
    document.querySelectorAll('[data-static-route-meta]').forEach(element => element.remove());
  }, []);

  // Filmstrip transition handler - used by View Details buttons
  const navigateWithTransition = useCallback((to) => {
    if (transitionPhase !== 'idle' || liftTargetRef.current) return;

    const nextLocation = getLocationDescriptor(to, location);

    /* Staying on the same page — a category picked from the Products menu
       while already on /products, say. Change the query and let the page
       animate whatever actually changed; there is no page to transition. */
    if (isSamePage(nextLocation, location)) {
      navigate(nextLocation);
      setDisplayLocation(nextLocation);
      return;
    }
    
    isFilmstripTransition.current = true;
    pendingLocationRef.current = nextLocation;
    
    // Phase 1: Exit animation - quick fade out
    setTransitionPhase('exit');
    setShowVignette(true);
    
    // Phase 2: Navigate BEFORE exit animation fully completes (overlap effect)
    setTimeout(() => {
      // Scroll to top before navigation
      window.scrollTo({ top: 0, behavior: 'instant' });
      
      navigate(nextLocation);
      setDisplayLocation(nextLocation);
      setTransitionPhase('enter');
      setShowFlash(true);
      
      // Remove flash after animation
      setTimeout(() => {
        setShowFlash(false);
      }, 100);
      
      // Phase 3: Complete transition
      setTimeout(() => {
        setTransitionPhase('idle');
        setShowVignette(false);
        isFilmstripTransition.current = false;
        pendingLocationRef.current = null;
      }, 300);
    }, 120); // Navigate earlier, during the exit animation
  }, [location, navigate, transitionPhase]);

  /* Press the call button and the floor moves. The departing page falls away
     downwards, the shaft passes, and the arriving page comes down from above
     and settles: one continuous motion, because a car going up makes the world
     outside go down.

     Only the hero's call button routes through here; every other link on the
     site still takes the filmstrip. */
  const navigateAsElevator = useCallback((to) => {
    if (transitionPhase !== 'idle' || liftTargetRef.current) return;

    const nextLocation = getLocationDescriptor(to, location);
    if (isSamePage(nextLocation, location)) {
      navigate(nextLocation);
      setDisplayLocation(nextLocation);
      return;
    }

    /* Two ways out of the ride, and both belong to the reader rather than to
       the design: a stated motion preference, and the shop's own lite mode. */
    const reducedMotion = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const liteMode = typeof document !== 'undefined'
      && document.documentElement.classList.contains('lite-mode');

    if (reducedMotion || liteMode) {
      navigateWithTransition(to);
      return;
    }

    const express = liftRidesRef.current > 0;
    const timings = express ? EXPRESS : FULL;
    liftRidesRef.current += 1;
    liftTargetRef.current = nextLocation;

    /* Read the bar before anything moves; once the page is travelling the
       measurement would be of a moving target. */
    const floors = measureNavFloors();
    const at = (ms, fn) => liftTimersRef.current.push(window.setTimeout(fn, ms));

    /* The ride clips the page to one screen, and a clipped element starts at
       the top of the document — so a reader who had scrolled would see the
       page snap upward on the first frame. Scrolling to the top and pushing
       <main> down by the same amount cancels out: nothing appears to move
       until the car does. */
    const scrolled = window.scrollY || 0;
    if (scrolled) window.scrollTo({ top: 0, behavior: 'instant' });

    setLift({ phase: 'depart', timings, floors, scroll: scrolled });

    /* The floor has cleared the viewport, so the swap cannot be seen. This is
       the only moment the route may change. */
    at(timings.depart, () => {
      window.scrollTo({ top: 0, behavior: 'instant' });
      /* Claim the route change so the effect below does not also run its own
         enter animation against a page nobody can see yet. */
      isFilmstripTransition.current = true;
      navigate(nextLocation);
      setDisplayLocation(nextLocation);
      /* On the new floor there is nothing to compensate for: the route just
         changed and the page starts at its top. */
      setLift((prev) => (prev ? { ...prev, phase: 'hold', scroll: 0 } : prev));
    });

    at(timings.depart + timings.hold, () => {
      setLift((prev) => (prev ? { ...prev, phase: 'arrive' } : prev));
    });

    at(liftTotal(timings), () => {
      isFilmstripTransition.current = false;
      liftTargetRef.current = null;
      setLift(null);
    });
  }, [location, navigate, navigateWithTransition, transitionPhase]);

  /* A ride still running after unmount would navigate a component that is
     already gone. */
  useEffect(() => () => {
    liftTimersRef.current.forEach((id) => window.clearTimeout(id));
    liftTimersRef.current = [];
  }, []);

  // Handle regular route changes (back/forward, direct navigation)
  useEffect(() => {
    if (isFilmstripTransition.current) return;
    
    /* The query moving on its own — the filter buttons write to the URL — is
       not a route change. displayLocation still has to follow it, or the next
       real navigation compares against a stale one. */
    if (isSamePage(location, displayLocation)) {
      if (getLocationSignature(location) !== getLocationSignature(displayLocation)) {
        setDisplayLocation(location);
      }
      return undefined;
    }

    if (getLocationSignature(location) !== getLocationSignature(displayLocation)) {
      // Use timeout to avoid synchronous setState in effect
      const timer = setTimeout(() => {
        setTransitionPhase('enter');
        setDisplayLocation(location);
        
        setTimeout(() => {
          setTransitionPhase('idle');
        }, 200);
      }, 0);
      
      return () => clearTimeout(timer);
    }
  }, [displayLocation, location]);

  // Handle hash scrolling when navigating to homepage with a hash (e.g., /#products)
  useEffect(() => {
    if (location.pathname === '/' && location.hash) {
      // Wait for the page to fully render (including lazy loaded components)
      const scrollToSection = () => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      };
      
      // Try multiple times with increasing delays to ensure content is loaded
      const timer1 = setTimeout(scrollToSection, 100);
      const timer2 = setTimeout(scrollToSection, 400);
      const timer3 = setTimeout(scrollToSection, 800);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
  }, [location.pathname, location.hash]);

  return (
    <PageTransitionContext.Provider value={{ navigateWithTransition, navigateAsElevator, transitionPhase, liftActive: !!lift }}>
      {/* Site-wide social defaults. Every route that wants its own simply
          declares the same property and Helmet replaces it — which is exactly
          what could not happen while these lived in index.html. */}
      <Helmet>
        <meta property="og:title" content="Retro Store — Premium FiveM Assets" />
        <meta property="og:description" content="Scripts, maps and peds for FiveM, escrow protected through Tebex." />
        <meta property="og:url" content="https://retrostore.dev/" />
        <meta property="og:image" content="https://retrostore.dev/img/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Retro Store — FiveM Assets. Perfected." />
        <meta name="twitter:title" content="Retro Store — Premium FiveM Assets" />
        <meta name="twitter:description" content="Scripts, maps and peds for FiveM, escrow protected through Tebex." />
        <meta name="twitter:image" content="https://retrostore.dev/img/og-image.png" />
      </Helmet>
      {/* The first thing in the tab order, and invisible until it has focus.
          Without it a keyboard user tabbed through the logo, the cart, the
          menu and five nav links on every single page before reaching the
          content. The target below has always been there. */}
      <a className="skip-to-content" href="#main-content">Skip to content</a>
      <LiftTransition
        active={!!lift}
        timings={lift?.timings || FULL}
        floors={lift?.floors}
      />

      {/* Filmstrip Vignette Overlay */}
      <div className={`filmstrip-vignette ${showVignette ? 'active' : ''}`} />
      
      {/* Film Advance Flash */}
      <div className={`filmstrip-flash ${showFlash ? 'active' : ''}`} />
      
      <div
        className={`page-transition-wrapper phase-${transitionPhase}${lift ? ` lift-${lift.phase}` : ''}`}
        inert={lift ? true : undefined}
        style={lift ? {
          '--lift-depart': `${lift.timings.depart}ms`,
          '--lift-arrive': `${lift.timings.arrive}ms`,
          '--lift-scroll': `${-(lift.scroll || 0)}px`
        } : undefined}
      >
        <Suspense fallback={
          <div className="page-loading-fallback" style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '100vh',
            background: 'transparent',
            opacity: transitionPhase === 'idle' ? 1 : 0
          }}>
            <div style={{ 
              width: '40px', 
              height: '40px', 
              border: '3px solid rgba(197, 183, 156, 0.2)',
              borderTop: '3px solid rgba(197, 183, 156, 1)',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }} />
          </div>
        }>
          <main id="main-content" tabIndex="-1">
            <Routes location={displayLocation}>
              <Route path="/" element={<Home />} />
              <Route path="/products" element={<AllProductsPage />} />
              <Route path="/bundles" element={<BundlesPage />} />
              <Route path="/product/:productId" element={<ProductRoute />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/partners" element={<Navigate to="/reviews" replace />} />
              <Route path="/team" element={<Team />} />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/checkout/cancel" element={<CheckoutCancel />} />
              <Route path="/checkout/auth-return" element={<CheckoutAuthReturn />} />
              {/* Admin Panel Routes */}
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Navigate to="meta-generator" replace />} />
                <Route path="login" element={<AdminLogin />} />
                <Route path="meta-generator" element={<MetaGeneratorPage />} />
                <Route path="versioning" element={<VersioningPage />} />
                <Route path="versioning/:scriptName" element={<VersioningPage />} />
              </Route>
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </main>
        </Suspense>
      </div>
    </PageTransitionContext.Provider>
  );
}

// Global Checkout Loading Overlay wrapper - minimal spinner only
const GlobalCheckoutOverlay = () => {
  const { showLoadingOverlay } = useCheckout();
  return (
    <Suspense fallback={null}>
      <CheckoutLoadingOverlay isVisible={showLoadingOverlay} />
    </Suspense>
  );
};

// Currency Validator - validates cart currency matches products currency
const CurrencyValidator = () => {
  const { productsCurrency } = useProducts();
  const { validateCartCurrency } = useCart();
  const hasValidated = useRef(false);

  useEffect(() => {
    // Only validate once when productsCurrency is first set
    if (productsCurrency && !hasValidated.current) {
      hasValidated.current = true;
      validateCartCurrency(productsCurrency);
    }
  }, [productsCurrency, validateCartCurrency]);

  return null;
};

function App() {
  return (
    <ErrorBoundary>
      <LiteModeProvider>
        <ProductsProvider>
          <CartProvider>
            <CheckoutProvider>
              <DiscordProvider>
                <AdminProvider>
                <HelmetProvider>
                <div className="App">
                  <BrowserRouter>
                    <AppContent />
                    {/* <CookieConsentBanner /> */}
                    {/* Global Checkout Loading Overlay - seamless UX */}
                    <GlobalCheckoutOverlay />
                    {/* Currency Validator - validates cart currency on load */}
                    <CurrencyValidator />
                    {/* Global Checkout Components */}
                    <Suspense fallback={null}>
                      <AuthenticationWidget />
                      {/* CheckoutModal replaced by native Tebex.js overlay */}
                    </Suspense>
                    {/* Discord Connect Modal - prompts users to link Discord */}
                    <Suspense fallback={null}>
                      <DiscordConnectModal />
                    </Suspense>
                    {/* Recent Buyers Widget - Social Proof */}
                    <StorefrontOnly>
                      <Suspense fallback={null}>
                        <RecentBuyersWidget />
                      </Suspense>
                    </StorefrontOnly>
                  </BrowserRouter>
                  {/* Toast notifications */}
                  <Suspense fallback={null}>
                    <LazyToaster
                      position="bottom-right"
                      toastOptions={{
                        style: {
                          background: '#1a1a1f',
                          border: '1px solid rgba(197, 183, 156, 0.2)',
                          color: '#f5f5f5'
                        }
                      }}
                    />
                  </Suspense>
                </div>
                </HelmetProvider>
                </AdminProvider>
              </DiscordProvider>
            </CheckoutProvider>
          </CartProvider>
        </ProductsProvider>
      </LiteModeProvider>
    </ErrorBoundary>
  );
}

export default App;
